import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGOS, NAV_LINKS, CONTACT } from "../data/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const orderHref = `https://maps.google.com/?q=${encodeURIComponent(CONTACT.mapsQuery)}`;

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-charcoal border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 sm:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img
            src={LOGOS.yellow}
            alt="Chicken Labb"
            className={`w-auto transition-all duration-300 ${scrolled ? "h-10" : "h-12 sm:h-14"}`}
          />
        </Link>

        <div className="hidden items-center gap-9 md:flex">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono text-xs uppercase tracking-widest transition-colors duration-150 hover:text-labyellow ${
                location.pathname === l.to ? "text-labyellow" : "text-offwhite"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href={orderHref}
            target="_blank"
            rel="noreferrer"
            data-testid="order-now-nav-button"
            className="rounded-full bg-labred px-6 py-2.5 font-display text-xs uppercase tracking-wider text-offwhite transition-all duration-150 hover:bg-labyellow hover:text-charcoal"
          >
            Order Now
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="text-offwhite md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden bg-charcoal md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="border-b border-white/10 py-3 font-mono text-sm uppercase tracking-widest text-offwhite"
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={orderHref}
                target="_blank"
                rel="noreferrer"
                className="mt-4 rounded-full bg-labred px-6 py-3 text-center font-display text-xs uppercase tracking-wider text-offwhite"
              >
                Order Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
